'use client'

import type * as React from 'react'
import type { LucideIcon } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { NavMain } from './nav-main'
import { NavUser } from './nav-user'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from './sidebar'

export type AppSidebarData = {
  user: {
    name: string
    email: string
    avatar: string
  }
  navMain: {
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    items?: {
      title: string
      url: string
    }[]
  }[]
  navSecondary?: {
    title: string
    url: string
    icon: LucideIcon
  }[]
  branding?: {
    title: string
    logo: React.ElementType
    href: string
  }
}

function NavSecondary({ items }: { items: AppSidebarData['navSecondary'] }) {
  const pathname = usePathname()

  if (!items || items.length === 0) {
    return null
  }

  return (
    <SidebarGroup className="mt-auto">
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                isActive={pathname?.startsWith(item.url)}
              >
                <Link href={item.url}>
                  <item.icon />
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

export function AppSidebar({
  data,
  onSignOut,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  data: AppSidebarData
  onSignOut?: () => void | Promise<void>
}) {
  return (
    <Sidebar
      collapsible="offcanvas"
      className="bg-sidebar select-none"
      style={{ backgroundColor: 'hsl(var(--sidebar))' }}
      {...props}
    >
      {data.branding && (
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex w-full items-center justify-between">
                <SidebarMenuButton
                  asChild
                  className="data-[slot=sidebar-menu-button]:!p-1.5 flex-1"
                >
                  <Link href={data.branding.href}>
                    <data.branding.logo className="!size-5" />
                    <span className="text-base font-semibold">
                      {data.branding.title}
                    </span>
                  </Link>
                </SidebarMenuButton>
                <SidebarTrigger className="ml-2 hidden md:flex" />
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
      )}
      <SidebarContent>
        <NavMain items={data.navMain} />
        <NavSecondary items={data.navSecondary} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} onSignOut={onSignOut} />
      </SidebarFooter>
    </Sidebar>
  )
}
